import { Vec3, ZERO, add, sub, length } from '../math/vec3'
import { CelestialBody } from '../world/body'
import { SYSTEM, bodyById } from '../world/system'
import { OrbitElements, stateFromElements } from './elements'
import { meanToTrue } from './kepler'

/** Gli elementi orbitali di un corpo celeste all'istante t. */
export function bodyElementsAt(body: CelestialBody, t: number): OrbitElements | null {
  if (!body.orbit || !body.parentId) return null
  const padre = bodyById(body.parentId)
  const n = Math.sqrt(padre.mu / Math.abs(body.orbit.sma) ** 3)
  const M = body.orbit.meanAnomalyAtEpoch + n * t
  return {
    sma: body.orbit.sma,
    ecc: body.orbit.ecc,
    inc: body.orbit.inc,
    raan: body.orbit.raan,
    argPe: body.orbit.argPe,
    trueAnomaly: meanToTrue(M, body.orbit.ecc),
    mu: padre.mu,
    epoch: t,
  }
}

/** Stato di un corpo rispetto al proprio PADRE. */
export function bodyStateAt(body: CelestialBody, t: number): { position: Vec3; velocity: Vec3 } {
  const el = bodyElementsAt(body, t)
  return el ? stateFromElements(el) : { position: ZERO, velocity: ZERO }
}

/** Posizione assoluta, cioe nel riferimento della radice del sistema. */
export function bodyAbsolutePosition(body: CelestialBody, t: number): Vec3 {
  if (!body.parentId) return ZERO
  return add(bodyAbsolutePosition(bodyById(body.parentId), t), bodyStateAt(body, t).position)
}

export function bodyAbsoluteVelocity(body: CelestialBody, t: number): Vec3 {
  if (!body.parentId) return ZERO
  return add(bodyAbsoluteVelocity(bodyById(body.parentId), t), bodyStateAt(body, t).velocity)
}

/** Posizione di un corpo vista dal riferimento di un altro. */
export const bodyPositionRelative = (bodyId: string, riferimentoId: string, t: number): Vec3 =>
  sub(bodyAbsolutePosition(bodyById(bodyId), t), bodyAbsolutePosition(bodyById(riferimentoId), t))

export const bodyVelocityRelative = (bodyId: string, riferimentoId: string, t: number): Vec3 =>
  sub(bodyAbsoluteVelocity(bodyById(bodyId), t), bodyAbsoluteVelocity(bodyById(riferimentoId), t))

/**
 * In quale sfera d'influenza cade un punto, dato che ora si sta usando come
 * riferimento `riferimento`.
 *
 * Restituisce il riferimento stesso se non si e ne entrati in un figlio ne
 * usciti verso il padre. Non fa il passaggio: dice solo dove si e.
 */
export function soiCorrente(
  positionRelativa: Vec3, riferimento: CelestialBody, t: number,
): CelestialBody {
  for (const figlio of Object.values(SYSTEM)) {
    if (figlio.parentId !== riferimento.id) continue
    const rel = sub(positionRelativa, bodyStateAt(figlio, t).position)
    if (length(rel) < figlio.soiRadius) return figlio
  }
  if (riferimento.parentId && length(positionRelativa) > riferimento.soiRadius) {
    return bodyById(riferimento.parentId)
  }
  return riferimento
}
